import { ImagePlus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MediaKind } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';

import { useMedia } from '../hooks/use-media';

import { CompactMediaPicker } from './compact-media-picker';
import { MediaThumb } from './media-thumb';

export interface MediaIdFieldProps {
  value: string | null;
  onChange: (mediaId: string | null) => void;
  /** Names the buttons for assistive tech, e.g. "hero image". */
  label: string;
  kind?: MediaKind;
  disabled?: boolean;
}

/**
 * A single media reference stored as an id: thumbnail, choose / change and remove. Choosing
 * opens the compact library picker and closes on the first pick.
 */
export function MediaIdField({
  value,
  onChange,
  label,
  kind = 'image',
  disabled = false,
}: MediaIdFieldProps) {
  const { t } = useTranslation('media');
  const [open, setOpen] = useState(false);
  const { data } = useMedia(value);

  return (
    <div className="flex items-center gap-4">
      <MediaThumb media={value ? data : null} className="size-20 shrink-0 rounded-md border" />
      <div className="flex flex-col items-start gap-2">
        {value ? (
          <span className="text-muted-foreground max-w-56 truncate text-xs">
            {data?.alt ?? t('untitled')}
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">{t('field.none')}</span>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            aria-label={
              value ? t('field.changeLabel', { label }) : t('field.chooseLabel', { label })
            }
            onClick={() => setOpen(true)}
          >
            <ImagePlus aria-hidden />
            {value ? t('field.change') : t('field.choose')}
          </Button>
          {value ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              aria-label={t('field.removeLabel', { label })}
              onClick={() => onChange(null)}
            >
              <X aria-hidden />
              {t('field.remove')}
            </Button>
          ) : null}
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('field.dialogTitle', { label })}</DialogTitle>
            <DialogDescription>{t('field.dialogDescription')}</DialogDescription>
          </DialogHeader>
          <CompactMediaPicker
            value={value}
            initialKind={kind}
            onChange={(mediaId) => {
              onChange(mediaId);
              setOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
